/**
 * @module @fanini/shared-api/queries/checkin
 * Query Factory für CheckIn-Entitäten (TanStack Query v5 Pattern).
 *
 * @example
 * ```tsx
 * const { data } = useQuery(checkinQueries.list('event-123'));
 * ```
 */
import { queryOptions } from '@tanstack/react-query';
import type { CheckIn } from '@fanini/shared-types';
import type { ApiClient } from '../client';

/**
 * Erstellt eine CheckIn Query Factory die einen API-Client nutzt.
 *
 * @param client - Typisierter API-Client
 * @returns CheckIn Query Factory
 */
export function createCheckinQueries(client: ApiClient) {
  return {
    /**
     * Root-Query-Key für alle CheckIn-Queries.
     * Wird für Cache-Invalidierungen auf oberster Ebene genutzt.
     */
    all: () => ['checkins'] as const,

    /**
     * Query für alle Check-ins eines Events.
     *
     * @param eventId - Event-ID
     * @returns TanStack Query Options
     */
    list: (eventId: string) =>
      queryOptions({
        queryKey: ['checkins', 'list', { eventId }] as const,
        queryFn: (): Promise<CheckIn[]> =>
          client.get<CheckIn[]>(`/api/checkins?eventId=${encodeURIComponent(eventId)}`),
      }),
  };
}

/** Typ der CheckIn Query Factory */
export type CheckinQueries = ReturnType<typeof createCheckinQueries>;
